import { Trans, useLingui } from "@lingui/react/macro";
import type {
  Bot,
  BotAbility,
  BotContact,
  BotContextSize,
  BotSecretMetadata,
  ModelCatalogEntry,
  SuperBotActivity,
  SuperBotScope,
  ThinkingLevel,
  VideoSize,
} from "@rakazo/contracts";
import {
  BOT_ABILITY_TOOLS,
  BOT_DESCRIPTION_MAX_LENGTH,
  BOT_MESSAGE_LIMIT_DEFAULTS,
  BOT_MESSAGE_LIMIT_MAX,
  BOT_NAME_MAX_LENGTH,
  BOT_SETTINGS_DEFAULTS,
  BOT_TITLE_MAX_LENGTH,
  BOT_VIDEO_CLIP_CAP_MAX,
  BUILTIN_TOOL_NAMES,
  botAbilityEnabled,
  defaultBotMessageRunLimit,
  isBuiltinToolName,
  resolveBotAvatarSet,
  SUPER_BOT_ACTIVITIES,
  SUPER_BOT_SCOPES,
  VIDEO_SIZES,
} from "@rakazo/contracts";
import {
  chatModelChoices,
  connectedModelChoices,
  credentialForModel,
  modelOptionKey,
  parseModelOptionKey,
  readAloudModelOptions,
  readAloudModelValue,
  resolveSelectableModelId,
} from "@rakazo/core";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Input,
  NativeSelect,
  NativeSelectOption,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  ToggleGroup,
  ToggleGroupItem,
} from "@rakazo/ui-web";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Pencil, Plus, Search, Settings, X } from "lucide-react";
import type { ReactNode } from "react";
import { Suspense, useId, useState } from "react";
import { BotAskersSettings } from "../../components/BotAskersSettings";
import { BotDailyCap } from "../../components/DailyCap";
import { ErrorBoundary, SectionLoadFailed } from "../../components/ErrorBoundary";
import { InstructionSettings } from "../../components/InstructionSettings";
import { PlainRulesSettings } from "../../components/PlainRulesSettings";
import { SettingsGroup, SettingsRow } from "../../components/SettingsRows";
import { StandingRules } from "../../components/StandingRules";
import { VaultSettings } from "../../components/VaultSettings";
import { botProfilePatch } from "../../lib/bot-profile-patch";
import { thinkingLevelLabel } from "../../lib/model-catalog";
import { lazyComponent } from "../../lib/preload-recovery";
import { useSpaceQueries } from "../../lib/query";
import { useSpaceRpc } from "../../lib/space-rpc";
import { errorText } from "../../lib/user-error";
import { AvatarStudioPopover } from "./avatar-studio-popover";
import type { BotSettingsActions } from "./bot-settings-dialog";
import { ChatModelPicker } from "./chat-model-picker";

const ScratchpadSection = lazyComponent(() =>
  import("../ScratchpadSection").then((module) => module.ScratchpadSection),
);
const KnowledgeSection = lazyComponent(() =>
  import("../KnowledgeSection").then((module) => module.KnowledgeSection),
);
const SemanticMemoryView = lazyComponent(() =>
  import("../SemanticMemoryView").then((module) => module.SemanticMemoryView),
);
const BotMemoryAccess = lazyComponent(() =>
  import("../MemoryZones").then((module) => module.BotMemoryAccess),
);
const AgentSkills = lazyComponent(() =>
  import("../KnowledgeSection").then((module) => module.AgentSkills),
);

/** A bot with every setting it has never changed filled in from the defaults. */
export type SettingsBot = Bot &
  Required<Pick<Bot, keyof typeof BOT_SETTINGS_DEFAULTS>> & {
    videoSizes: VideoSize[];
  };

export function withSettingDefaults(bot: Bot): SettingsBot {
  return {
    ...bot,
    contextSize: bot.contextSize ?? BOT_SETTINGS_DEFAULTS.contextSize,
    seeImages: bot.seeImages ?? BOT_SETTINGS_DEFAULTS.seeImages,
    videoSizes: bot.videoSizes ?? [...BOT_SETTINGS_DEFAULTS.videoSizes],
    videoApproveEach: bot.videoApproveEach ?? BOT_SETTINGS_DEFAULTS.videoApproveEach,
    videoClipCap: bot.videoClipCap ?? BOT_SETTINGS_DEFAULTS.videoClipCap,
    superBot: bot.superBot ?? BOT_SETTINGS_DEFAULTS.superBot,
    superBotActivity: bot.superBotActivity ?? BOT_SETTINGS_DEFAULTS.superBotActivity,
    superBotScope: bot.superBotScope ?? BOT_SETTINGS_DEFAULTS.superBotScope,
  };
}

type SectionProps = { bot: SettingsBot; actions: BotSettingsActions };

/** A message_bot limit: empty uses the default, shown as the placeholder. */
function BotMessageLimit(props: {
  id: string;
  label: ReactNode;
  value: number | null;
  fallback: number;
  max: number;
  onSave: (value: number | null) => Promise<unknown>;
}) {
  const shown = props.value === null ? "" : String(props.value);
  return (
    <Field label={props.label} htmlFor={props.id}>
      <Input
        id={props.id}
        type="number"
        min={1}
        max={props.max}
        placeholder={String(props.fallback)}
        defaultValue={shown}
        key={shown}
        onBlur={(event) => {
          const raw = event.target.value.trim();
          const next = raw === "" ? null : Number(raw);
          if (next === props.value || (next !== null && !Number.isInteger(next))) return;
          void props.onSave(next).catch(() => (event.target.value = shown));
        }}
      />
    </Field>
  );
}

const segment = "h-8 flex-1 px-3 text-[13px] aria-pressed:bg-background aria-pressed:shadow-sm";
const fieldLabel = "mb-1.5 block text-[13px] text-muted-foreground";

/** Saves a text field when it loses focus or on Enter, and only when it changed. */
function CommitInput({
  value,
  onCommit,
  multiline = false,
  ...props
}: {
  id: string;
  value: string;
  onCommit: (value: string) => Promise<void>;
  multiline?: boolean;
  maxLength: number;
  placeholder?: string;
  "aria-label"?: string;
}) {
  const [draft, setDraft] = useState(value);
  const [shown, setShown] = useState(value);
  if (shown !== value) {
    setShown(value);
    setDraft(value);
  }
  const commit = () => {
    if (draft.trim() === value.trim()) return;
    void onCommit(draft.trim()).catch(() => setDraft(value));
  };
  return multiline ? (
    <Textarea
      {...props}
      value={draft}
      rows={3}
      className="min-h-20 resize-none field-sizing-content"
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
    />
  ) : (
    <Input
      {...props}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.nativeEvent.isComposing || event.keyCode === 229) {
          if (event.key === "Escape") event.stopPropagation();
          return;
        }
        if (event.key === "Enter") {
          event.preventDefault();
          commit();
        }
        if (event.key === "Escape") setDraft(value);
      }}
    />
  );
}

function Field({
  label,
  htmlFor,
  action,
  children,
}: {
  label: ReactNode;
  htmlFor: string;
  /** A control beside the label, kept out of it so the field's name stays the label alone. */
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between">
        <label htmlFor={htmlFor} className={fieldLabel}>
          {label}
        </label>
        {action}
      </div>
      {children}
    </div>
  );
}

export function ProfileSection({ bot, actions }: SectionProps) {
  const { t } = useLingui();
  const ids = useId();
  return (
    <div className="space-y-6" data-testid="bot-settings-profile">
      <SettingsGroup>
        <div className="flex justify-center py-5">
          <AvatarStudioPopover
            value={bot.color}
            identity={bot.id}
            status={bot.status}
            size={64}
            avatarSet={resolveBotAvatarSet(bot.avatarSet)}
            onChange={(color, avatarSet) =>
              void actions.save({ color, ...(avatarSet !== undefined ? { avatarSet } : {}) })
            }
          />
        </div>
        <Field label={<Trans>Name</Trans>} htmlFor={`${ids}-name`}>
          <CommitInput
            id={`${ids}-name`}
            value={bot.name}
            maxLength={BOT_NAME_MAX_LENGTH}
            onCommit={async (name) => {
              if (name) await actions.save({ name });
            }}
          />
        </Field>
        <Field label={<Trans>Title</Trans>} htmlFor={`${ids}-title`}>
          <CommitInput
            id={`${ids}-title`}
            value={bot.title}
            maxLength={BOT_TITLE_MAX_LENGTH}
            placeholder={t`e.g. Presales`}
            onCommit={(title) => actions.save({ title })}
          />
        </Field>
        <Field label={<Trans>Description</Trans>} htmlFor={`${ids}-description`}>
          <CommitInput
            id={`${ids}-description`}
            multiline
            value={bot.description}
            maxLength={BOT_DESCRIPTION_MAX_LENGTH}
            onCommit={(description) =>
              actions.save(botProfilePatch(bot.description ?? "", description))
            }
          />
        </Field>
        {bot.access !== "chat" ? (
          <SwitchRow
            label={<Trans>Share with Space</Trans>}
            testId="bot-settings-share"
            checked={bot.visibility === "space"}
            onChange={(shared) => void actions.save({ visibility: shared ? "space" : "private" })}
          />
        ) : null}
      </SettingsGroup>
      <StandingRules botId={bot.id} />
      {bot.visibility === "space" && bot.access !== "chat" ? (
        <BotAskersSettings botId={bot.id} />
      ) : null}
      <InstructionSettings key={bot.id} bot={bot} />
    </div>
  );
}

const NO_CATALOG: ModelCatalogEntry[] = [];

/**
 * The model pickers' shared data: connected choices, what Default runs and thinking levels. With
 * a bot, Default is what that bot runs, which for Chief and the setup leads can be a stronger
 * model than the member's main one.
 */
function useModelOptions(provider: string | null, modelId: string | null, botId?: string) {
  const { t } = useLingui();
  const queries = useSpaceQueries();
  const refresh = { refetchOnMount: "always" } as const;
  const credentialsQuery = useQuery({ ...queries.models.credentials.queryOptions(), ...refresh });
  const catalogQuery = useQuery({ ...queries.models.list.queryOptions(), ...refresh });
  const me = useQuery({ ...queries.me.queryOptions(), ...refresh }).data ?? null;
  const botDefault = useQuery({
    ...queries.bots.defaultModel.queryOptions({ input: { botId: botId ?? "" } }),
    ...refresh,
    enabled: Boolean(botId),
  }).data;
  const defaultProvider = botDefault ? botDefault.provider : (me?.defaultProvider ?? null);
  const defaultModelId = botDefault ? botDefault.modelId : (me?.defaultModel ?? null);
  const credentials = credentialsQuery.data ?? [];
  const catalog = catalogQuery.data ?? NO_CATALOG;
  const selected =
    provider && modelId
      ? { provider, modelId: resolveSelectableModelId(catalog, provider, modelId) }
      : null;
  const selectedKey = selected ? modelOptionKey(selected.provider, selected.modelId) : "";
  const defaultEntry = defaultModelId
    ? catalog.find((entry) => entry.provider === defaultProvider && entry.id === defaultModelId)
    : undefined;
  const defaultName = defaultModelId ? (defaultEntry?.label ?? defaultModelId) : null;
  const choices = (fallbackLabel: string) =>
    chatModelChoices(
      connectedModelChoices(credentials, catalog),
      selectedKey,
      fallbackLabel,
      defaultEntry?.images,
    );
  const effectiveProvider = selected?.provider ?? defaultProvider;
  const effectiveModelId = selected?.modelId ?? defaultModelId;
  const effectiveEntry = catalog.find(
    (entry) =>
      entry.provider === effectiveProvider &&
      resolveSelectableModelId(catalog, entry.provider, entry.id) === effectiveModelId,
  );
  const credential = credentialForModel(credentials, effectiveProvider, effectiveModelId);
  return {
    t,
    me,
    credentials,
    selectedKey,
    defaultName,
    choices,
    thinkingOptions: (credential?.thinkingLevels ?? effectiveEntry?.thinkingLevels ?? []).filter(
      (level) => level !== "off",
    ),
    defaultThinking: credential?.thinkingLevel ?? "medium",
    ready: credentialsQuery.data !== undefined && catalogQuery.data !== undefined,
  };
}

export function ModelSection({ bot, actions }: SectionProps) {
  const { t } = useLingui();
  const ids = useId();
  const main = useModelOptions(bot.modelProvider, bot.modelId, bot.id);
  const talk = useModelOptions(bot.talkModelProvider ?? null, bot.talkModelId ?? null);
  const defaultName = main.defaultName;
  const ownName = main.me?.name || t`You`;
  const payingOptions = bot.modelProvider
    ? main.credentials.filter((credential) => credential.provider === bot.modelProvider)
    : [];
  const pick = (key: string) => (key ? parseModelOptionKey(key) : null);
  return (
    <div className="space-y-6" data-testid="bot-settings-model">
      <SettingsGroup>
        <Field
          label={<Trans>Model</Trans>}
          action={
            actions.onOpenConnections ? (
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={t`Model connections`}
                onClick={actions.onOpenConnections}
              >
                <Settings className="size-4" />
              </Button>
            ) : null
          }
          htmlFor={`${ids}-model`}
        >
          <ChatModelPicker
            id={`${ids}-model`}
            choices={main.choices(defaultName ? t`Default (${defaultName})` : t`Default`)}
            value={main.selectedKey}
            onChange={(key) => {
              const next = pick(key);
              void actions.save({
                modelProvider: next?.provider ?? null,
                modelId: next?.modelId ?? null,
                ...(main.ready ? { thinkingLevel: null } : {}),
              });
            }}
          />
        </Field>
        <Field label={<Trans>Talk model</Trans>} htmlFor={`${ids}-talk-model`}>
          <ChatModelPicker
            id={`${ids}-talk-model`}
            choices={talk.choices(t`Same as model`)}
            value={talk.selectedKey}
            onChange={(key) => {
              const next = pick(key);
              void actions.save({
                talkModelProvider: next?.provider ?? null,
                talkModelId: next?.modelId ?? null,
              });
            }}
          />
        </Field>
        {main.thinkingOptions.length ? (
          <Field label={<Trans>Thinking</Trans>} htmlFor={`${ids}-thinking`}>
            <NativeSelect
              id={`${ids}-thinking`}
              className="w-full"
              value={bot.thinkingLevel ?? ""}
              onChange={(event) =>
                void actions.save({
                  thinkingLevel: (event.target.value || null) as ThinkingLevel | null,
                })
              }
            >
              <NativeSelectOption value="">
                {t`Default (${thinkingLevelLabel(main.defaultThinking)})`}
              </NativeSelectOption>
              {main.thinkingOptions.map((level) => (
                <NativeSelectOption key={level} value={level}>
                  {thinkingLevelLabel(level)}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
        ) : null}
        <div className="px-4 py-3">
          <span id={`${ids}-context`} className={fieldLabel}>
            <Trans>Context</Trans>
          </span>
          <ToggleGroup
            aria-labelledby={`${ids}-context`}
            value={[bot.contextSize]}
            onValueChange={([next]) => {
              if (next && next !== bot.contextSize)
                void actions.save({ contextSize: next as BotContextSize });
            }}
            spacing={1}
            className="w-full rounded-lg bg-muted p-0.5"
          >
            <ToggleGroupItem value="short" className={segment}>
              <Trans>Short</Trans>
            </ToggleGroupItem>
            <ToggleGroupItem value="standard" className={segment}>
              <Trans>Standard</Trans>
            </ToggleGroupItem>
            <ToggleGroupItem value="long" className={segment}>
              <Trans>Long</Trans>
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
        <ImageModelField bot={bot} actions={actions} />
        <Field label={<Trans>Key owner</Trans>} htmlFor={`${ids}-key-owner`}>
          <NativeSelect
            id={`${ids}-key-owner`}
            className="w-full"
            disabled={!bot.modelProvider}
            value={bot.modelCredentialId ?? ""}
            onChange={(event) =>
              void actions.save({ modelCredentialId: event.target.value || null })
            }
          >
            <NativeSelectOption value="">{t`${ownName} · Automatic`}</NativeSelectOption>
            {payingOptions.map((credential) => (
              <NativeSelectOption key={credential.id} value={credential.id}>
                {`${ownName} · ${credential.label}`}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
      </SettingsGroup>
    </div>
  );
}

function SwitchRow({
  label,
  checked,
  onChange,
  testId,
  disabled,
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  testId?: string;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <SettingsRow
      label={<label htmlFor={id}>{label}</label>}
      control={
        <Switch
          id={id}
          data-testid={testId}
          checked={checked}
          disabled={disabled}
          onCheckedChange={onChange}
        />
      }
    />
  );
}

export function AbilitiesSection({ bot, actions }: SectionProps) {
  const { t } = useLingui();
  const ids = useId();
  const disabled = bot.disabledBuiltinTools ?? [];
  const on = (ability: BotAbility) => botAbilityEnabled(disabled, ability);
  const computer = on("computer") ? bot.computerMode : "off";
  const toggle = (ability: BotAbility) => (enabled: boolean) =>
    void actions.setAbility(ability, enabled);
  const otherTools = disabled.filter(
    (name) => isBuiltinToolName(name) && !ABILITY_TOOL_NAMES.has(name),
  );
  return (
    <div className="space-y-6" data-testid="bot-settings-abilities">
      <SettingsGroup>
        <div className="px-4 py-3">
          <span id={`${ids}-computer`} className={fieldLabel}>
            <Trans>Computer</Trans>
          </span>
          <ToggleGroup
            aria-labelledby={`${ids}-computer`}
            value={[computer]}
            onValueChange={async ([next]) => {
              if (!next || next === computer) return;
              if (next === "off") return void actions.setAbility("computer", false);
              if (computer === "off") await actions.setAbility("computer", true);
              if (next !== bot.computerMode) await actions.setComputer(next as Bot["computerMode"]);
            }}
            spacing={1}
            className="w-full rounded-lg bg-muted p-0.5"
          >
            <ToggleGroupItem value="off" className={segment}>
              <Trans>Off</Trans>
            </ToggleGroupItem>
            <ToggleGroupItem value="team" className={segment}>
              <Trans>Team</Trans>
            </ToggleGroupItem>
            <ToggleGroupItem value="dedicated" className={segment}>
              <Trans>Private</Trans>
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
        <SwitchRow
          label={<Trans>Browser</Trans>}
          checked={on("browser")}
          onChange={toggle("browser")}
        />
        <SwitchRow
          label={<Trans>Read documents</Trans>}
          checked={on("readDocuments")}
          onChange={toggle("readDocuments")}
        />
        <SwitchRow
          label={<Trans>See images</Trans>}
          checked={bot.seeImages}
          onChange={(seeImages) => void actions.save({ seeImages })}
        />
        <SwitchRow
          label={<Trans>Transcribe voice</Trans>}
          checked={bot.transcribeVoice ?? false}
          onChange={(transcribeVoice) => void actions.save({ transcribeVoice })}
          testId="ability-transcribe-voice"
        />
        <SwitchRow
          label={<Trans>Make images</Trans>}
          checked={on("makeImages")}
          onChange={toggle("makeImages")}
        />
        <SwitchRow
          label={<Trans>Make videos</Trans>}
          checked={on("makeVideos")}
          onChange={toggle("makeVideos")}
          testId="ability-make-videos"
        />
        {on("makeVideos") ? (
          <>
            <VideoModelFields bot={bot} actions={actions} />
            <div className="px-4 py-3">
              <span id={`${ids}-sizes`} className={fieldLabel}>
                <Trans>Sizes</Trans>
              </span>
              <ToggleGroup
                aria-labelledby={`${ids}-sizes`}
                multiple
                value={bot.videoSizes}
                onValueChange={(next) => {
                  if (next.length)
                    void actions.save({
                      videoSizes: VIDEO_SIZES.filter((size) => next.includes(size)),
                    });
                }}
                spacing={1}
                className="flex-wrap"
              >
                {VIDEO_SIZES.map((size) => (
                  <ToggleGroupItem
                    key={size}
                    value={size}
                    variant="outline"
                    size="sm"
                    className="uppercase"
                  >
                    {size}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </div>
            <SwitchRow
              label={<Trans>Approve each video</Trans>}
              checked={bot.videoApproveEach}
              onChange={(videoApproveEach) => void actions.save({ videoApproveEach })}
            />
            <Field label={<Trans>Clip cap</Trans>} htmlFor={`${ids}-clip-cap`}>
              <Input
                id={`${ids}-clip-cap`}
                type="number"
                min={1}
                max={BOT_VIDEO_CLIP_CAP_MAX}
                defaultValue={bot.videoClipCap}
                key={bot.videoClipCap}
                onBlur={(event) => {
                  const value = Number(event.target.value);
                  if (Number.isInteger(value) && value !== bot.videoClipCap)
                    void actions
                      .save({ videoClipCap: value })
                      .catch(() => (event.target.value = String(bot.videoClipCap)));
                }}
              />
            </Field>
          </>
        ) : null}
        <BotDailyCap botId={bot.id} />
      </SettingsGroup>
      <SettingsGroup>
        <SwitchRow
          label={<Trans>Manage the Nexa Mates it creates</Trans>}
          checked={bot.fleetManagement}
          onChange={(fleetManagement) => void actions.save({ fleetManagement })}
        />
        <SwitchRow
          label={<Trans>Allow cross-space Nexa Mate communication</Trans>}
          checked={bot.crossSpaceCommunication ?? true}
          onChange={(crossSpaceCommunication) => void actions.save({ crossSpaceCommunication })}
        />
        <BotMessageLimit
          id={`${ids}-bot-messages-run`}
          label={<Trans>Nexa Mate messages per run</Trans>}
          value={bot.botMessageRunLimit ?? null}
          fallback={defaultBotMessageRunLimit(bot.superBot)}
          max={BOT_MESSAGE_LIMIT_MAX.perRun}
          onSave={(botMessageRunLimit) => actions.save({ botMessageRunLimit })}
        />
        <BotMessageLimit
          id={`${ids}-bot-messages-day`}
          label={<Trans>Nexa Mate messages per day</Trans>}
          value={bot.botMessageDailyLimit ?? null}
          fallback={BOT_MESSAGE_LIMIT_DEFAULTS.perDay}
          max={BOT_MESSAGE_LIMIT_MAX.perDay}
          onSave={(botMessageDailyLimit) => actions.save({ botMessageDailyLimit })}
        />
        <div className="space-y-2 px-4 py-3">
          <NativeSelect
            aria-label={t`Disable a tool`}
            value=""
            className="w-full"
            onChange={(event) => {
              const name = event.target.value;
              if (isBuiltinToolName(name))
                void actions.save({ disabledBuiltinTools: [...disabled, name] });
            }}
          >
            <NativeSelectOption value="">{t`Disable a tool`}</NativeSelectOption>
            {BUILTIN_TOOL_NAMES.filter(
              (name) => !disabled.includes(name) && !ABILITY_TOOL_NAMES.has(name),
            ).map((name) => (
              <NativeSelectOption key={name} value={name}>
                {humanizeTool(name)}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          {otherTools.map((name) => (
            <div key={name} className="flex min-h-9 items-center justify-between text-sm">
              <span>{humanizeTool(name)}</span>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={t`Enable ${humanizeTool(name)}`}
                onClick={() =>
                  void actions.save({
                    disabledBuiltinTools: disabled.filter((entry) => entry !== name),
                  })
                }
              >
                <X className="size-4" />
              </Button>
            </div>
          ))}
        </div>
      </SettingsGroup>
      <PlainRulesSettings botId={bot.id} title={<Trans>Approval rules</Trans>} />
    </div>
  );
}

/**
 * The bot's own image model from the image role's models on the owner's connections. A
 * deployment endpoint id saved before the picker stays listed until it is changed.
 */
function ImageModelField({ bot, actions }: SectionProps) {
  const { t } = useLingui();
  const id = useId();
  const queries = useSpaceQueries();
  const models =
    useQuery(queries.models.forRole.queryOptions({ input: { role: "image" } })).data ?? [];
  const credentials = useQuery(queries.models.credentials.queryOptions()).data ?? [];
  const connected = new Set(credentials.map((credential) => credential.provider));
  const legacy = bot.imageModel && !bot.imageModelProvider ? bot.imageModel : null;
  const selected =
    bot.imageModelProvider && bot.imageModel
      ? modelOptionKey(bot.imageModelProvider, bot.imageModel)
      : (legacy ?? "");
  return (
    <Field label={<Trans>Image model</Trans>} htmlFor={id}>
      <NativeSelect
        id={id}
        className="w-full"
        value={selected}
        onChange={(event) => {
          const value = event.target.value;
          if (value === legacy) return;
          const next = value ? parseModelOptionKey(value) : null;
          void actions.save({
            imageModel: next?.modelId ?? null,
            imageModelProvider: next?.provider ?? null,
          });
        }}
      >
        <NativeSelectOption value="">{t`Person default`}</NativeSelectOption>
        {legacy ? <NativeSelectOption value={legacy}>{legacy}</NativeSelectOption> : null}
        {models
          .filter((model) => connected.has(model.provider))
          .map((model) => (
            <NativeSelectOption
              key={modelOptionKey(model.provider, model.id)}
              value={modelOptionKey(model.provider, model.id)}
            >
              {model.label}
            </NativeSelectOption>
          ))}
      </NativeSelect>
    </Field>
  );
}

/** The bot's own video model from the video role's models, and the connection that pays. */
function VideoModelFields({ bot, actions }: SectionProps) {
  const { t } = useLingui();
  const ids = useId();
  const queries = useSpaceQueries();
  const models =
    useQuery(queries.models.forRole.queryOptions({ input: { role: "video" } })).data ?? [];
  const credentials = useQuery(queries.models.credentials.queryOptions()).data ?? [];
  const me = useQuery(queries.me.queryOptions()).data ?? null;
  const ownName = me?.name || t`You`;
  const selected =
    bot.videoModelProvider && bot.videoModelId
      ? modelOptionKey(bot.videoModelProvider, bot.videoModelId)
      : "";
  const paying = bot.videoModelProvider
    ? credentials.filter((credential) => credential.provider === bot.videoModelProvider)
    : [];
  return (
    <>
      <Field label={<Trans>Video model</Trans>} htmlFor={`${ids}-video-model`}>
        <NativeSelect
          id={`${ids}-video-model`}
          className="w-full"
          value={selected}
          onChange={(event) => {
            const next = event.target.value ? parseModelOptionKey(event.target.value) : null;
            void actions.save({
              videoModelProvider: next?.provider ?? null,
              videoModelId: next?.modelId ?? null,
            });
          }}
        >
          <NativeSelectOption value="">{t`Person default`}</NativeSelectOption>
          {models.map((model) => (
            <NativeSelectOption
              key={modelOptionKey(model.provider, model.id)}
              value={modelOptionKey(model.provider, model.id)}
            >
              {model.label}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Field>
      {paying.length > 1 ? (
        <Field label={<Trans>Key owner</Trans>} htmlFor={`${ids}-video-key-owner`}>
          <NativeSelect
            id={`${ids}-video-key-owner`}
            className="w-full"
            value={bot.videoModelCredentialId ?? ""}
            onChange={(event) =>
              void actions.save({ videoModelCredentialId: event.target.value || null })
            }
          >
            <NativeSelectOption value="">{t`${ownName} · Automatic`}</NativeSelectOption>
            {paying.map((credential) => (
              <NativeSelectOption key={credential.id} value={credential.id}>
                {`${ownName} · ${credential.label}`}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
      ) : null}
    </>
  );
}

/** Tools an ability switch covers; the disable-a-tool list leaves them to their switch. */
const ABILITY_TOOL_NAMES: ReadonlySet<string> = new Set(Object.values(BOT_ABILITY_TOOLS).flat());

function humanizeTool(name: string) {
  const words = name.replaceAll("_", " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The Space's team lead switch, for Space admins; others see whether the bot leads. The Super Nexa Mate
 * is shared with the Space and there is one per Space, so turning it on asks first when that
 * shares this bot or moves the role from `currentSuperBot`. The server enforces both.
 */
export function SuperBotSection({
  bot,
  actions,
  currentSuperBot,
}: SectionProps & { currentSuperBot?: string }) {
  const { t } = useLingui();
  const ids = useId();
  const queries = useSpaceQueries();
  // The memory zones view carries the one server answer to "is this member a Space admin".
  const admin = useQuery(queries.memoryZones.list.queryOptions()).data?.canManage ?? false;
  const [confirm, setConfirm] = useState(false);
  const shares = bot.visibility !== "space";
  const labels: Record<SuperBotActivity, string> = {
    quiet: t`Quiet`,
    steady: t`Steady`,
    keen: t`Keen`,
  };
  const scopes: Record<SuperBotScope, string> = {
    all: t`All shared Nexa Mates`,
    created: t`Nexa Mates it created`,
  };
  return (
    <SettingsGroup data-testid="bot-settings-super-bot">
      <SwitchRow
        label={<Trans>Super Nexa Mate</Trans>}
        checked={bot.superBot}
        disabled={!admin}
        onChange={(superBot) => {
          if (superBot && (shares || currentSuperBot)) setConfirm(true);
          else void actions.save({ superBot });
        }}
        testId="super-bot-switch"
      />
      {bot.superBot && admin ? (
        <>
          <Field label={<Trans>Activity</Trans>} htmlFor={`${ids}-activity`}>
            <NativeSelect
              id={`${ids}-activity`}
              className="w-full"
              value={bot.superBotActivity}
              onChange={(event) =>
                void actions.save({ superBotActivity: event.target.value as SuperBotActivity })
              }
            >
              {SUPER_BOT_ACTIVITIES.map((activity) => (
                <NativeSelectOption key={activity} value={activity}>
                  {labels[activity]}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <Field label={<Trans>Watch</Trans>} htmlFor={`${ids}-scope`}>
            <NativeSelect
              id={`${ids}-scope`}
              className="w-full"
              value={bot.superBotScope}
              onChange={(event) =>
                void actions.save({ superBotScope: event.target.value as SuperBotScope })
              }
            >
              {SUPER_BOT_SCOPES.map((scope) => (
                <NativeSelectOption key={scope} value={scope}>
                  {scopes[scope]}
                </NativeSelectOption>
              ))}
            </NativeSelect>
            <p className="mt-2 text-[13px] text-muted-foreground">
              <Trans>Wakes on its own. Speaks only when something needs you.</Trans>
            </p>
          </Field>
        </>
      ) : null}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Make {bot.name} the Super Nexa Mate?</Trans>
            </AlertDialogTitle>
            {shares ? (
              <AlertDialogDescription>
                <Trans>
                  It will be shared with the Space, and everyone here will see its chat.
                </Trans>
              </AlertDialogDescription>
            ) : null}
            {currentSuperBot ? (
              <AlertDialogDescription>
                <Trans>{currentSuperBot} stops being the Super Nexa Mate.</Trans>
              </AlertDialogDescription>
            ) : null}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              <Trans>Cancel</Trans>
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="super-bot-confirm"
              onClick={() => {
                setConfirm(false);
                void actions.save({
                  superBot: true,
                  ...(currentSuperBot ? { replaceSuperBot: true } : {}),
                  ...(shares ? { visibility: "space" as const } : {}),
                });
              }}
            >
              <Trans>Make Super Nexa Mate</Trans>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsGroup>
  );
}

export function NotificationsSection({ bot, actions }: SectionProps) {
  const { t } = useLingui();
  const ids = useId();
  const queries = useSpaceQueries();
  const speechProvider = bot.speechModelProvider ?? null;
  const voices = useQuery({
    // The bot's voice belongs to its read-aloud model's provider, else the account's.
    ...queries.voice.voices.queryOptions({
      input: speechProvider ? { provider: speechProvider } : {},
    }),
    refetchOnMount: "always",
  }).data;
  const catalog = useQuery(queries.voice.catalog.queryOptions()).data;
  const credentials = useQuery(queries.voice.credentials.queryOptions({ input: {} })).data;
  const models = readAloudModelOptions(
    catalog ?? [],
    (credentials ?? []).map((credential) => credential.provider),
  );
  const modelValue = readAloudModelValue(speechProvider, bot.speechModelId ?? null);
  return (
    <SettingsGroup data-testid="bot-settings-notifications">
      <SwitchRow
        label={<Trans>Notifications</Trans>}
        checked={bot.notifyOnFinish}
        onChange={(notifyOnFinish) => void actions.save({ notifyOnFinish })}
      />
      <SwitchRow
        label={<Trans>Read replies aloud</Trans>}
        checked={bot.autoSpeak}
        onChange={(autoSpeak) => void actions.save({ autoSpeak })}
      />
      {models.length ? (
        <Field label={<Trans>Read-aloud model</Trans>} htmlFor={`${ids}-speech-model`}>
          <NativeSelect
            id={`${ids}-speech-model`}
            data-testid="bot-speech-model"
            className="w-full"
            value={modelValue}
            onChange={(event) => {
              const picked = models.find((model) => model.value === event.target.value);
              void actions.save({
                speechModelProvider: picked?.provider ?? null,
                speechModelId: picked?.modelId ?? null,
              });
            }}
          >
            <NativeSelectOption value="">{t`Account default`}</NativeSelectOption>
            {modelValue && !models.some((model) => model.value === modelValue) ? (
              <NativeSelectOption value={modelValue}>{bot.speechModelId}</NativeSelectOption>
            ) : null}
            {models.map((model) => (
              <NativeSelectOption key={model.value} value={model.value}>
                {model.label}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
      ) : null}
      {voices?.length ? (
        <Field label={<Trans>Voice</Trans>} htmlFor={`${ids}-voice`}>
          <NativeSelect
            id={`${ids}-voice`}
            className="w-full"
            value={bot.voiceId ?? ""}
            onChange={(event) => void actions.save({ voiceId: event.target.value || null })}
          >
            <NativeSelectOption value="">{t`Account default`}</NativeSelectOption>
            {voices.map((voice) => (
              <NativeSelectOption key={voice.id} value={voice.id}>
                {voice.label}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
      ) : null}
    </SettingsGroup>
  );
}

export function MemorySection({ bot }: SectionProps) {
  const queries = useSpaceQueries();
  const [semanticOpen, setSemanticOpen] = useState(false);
  // A parent can share notes with the bots it made.
  const hasSubBots = (useQuery(queries.bots.list.queryOptions()).data ?? []).some(
    (other) => other.parentBotId === bot.id,
  );
  const lazy = (node: ReactNode) => (
    <ErrorBoundary fallback={<SectionLoadFailed />}>
      <Suspense fallback={null}>{node}</Suspense>
    </ErrorBoundary>
  );
  return (
    <div className="space-y-6" data-testid="bot-settings-memory">
      {lazy(<ScratchpadSection botId={bot.id} />)}
      <Tabs defaultValue="memory">
        <TabsList>
          <TabsTrigger value="memory">
            <Trans>Memory</Trans>
          </TabsTrigger>
          <TabsTrigger value="skills">
            <Trans>Skills</Trans>
          </TabsTrigger>
        </TabsList>
        <TabsContent value="memory" className="pt-3">
          {lazy(<KnowledgeSection botId={bot.id} shareDown={hasSubBots} />)}
          {semanticOpen ? (
            lazy(<SemanticMemoryView botId={bot.id} />)
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="mt-2"
              onClick={() => setSemanticOpen(true)}
            >
              <Trans>View semantic memory</Trans>
            </Button>
          )}
          {lazy(<BotMemoryAccess botId={bot.id} />)}
        </TabsContent>
        <TabsContent value="skills" className="pt-3">
          {lazy(<AgentSkills botId={bot.id} />)}
        </TabsContent>
      </Tabs>
    </div>
  );
}

type VaultTab = "all" | "logins" | "keys";

/** The vault this bot's Space uses, by kind. Cards and IDs are not credential kinds yet. */
export function VaultSection() {
  const { t } = useLingui();
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState<VaultTab>("all");
  const needle = query.trim().toLowerCase();
  const filter = (secret: BotSecretMetadata) =>
    (tab === "all" ||
      (tab === "logins" ? secret.auth?.type === "login" : secret.auth?.type !== "login")) &&
    (!needle ||
      secret.name.toLowerCase().includes(needle) ||
      secret.origin.toLowerCase().includes(needle));
  return (
    <div className="space-y-4" data-testid="bot-settings-vault">
      <SearchField value={query} onChange={setQuery} label={t`Search`} />
      <Tabs value={tab} onValueChange={(next) => setTab(next as VaultTab)}>
        <TabsList className="w-full">
          <TabsTrigger value="all">
            <Trans>All</Trans>
          </TabsTrigger>
          <TabsTrigger value="logins">
            <Trans>Logins</Trans>
          </TabsTrigger>
          <TabsTrigger value="keys">
            <Trans>API keys</Trans>
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <VaultSettings filter={filter} />
    </div>
  );
}

function SearchField({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
}) {
  return (
    <div className="relative min-w-0 flex-1">
      <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        type="search"
        aria-label={label}
        placeholder={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="ps-9"
      />
    </div>
  );
}

type ContactDraft = { name: string; email: string; phone: string; note: string };
const EMPTY_CONTACT: ContactDraft = { name: "", email: "", phone: "", note: "" };

export function AddressBookSection({ botId }: { botId: string }) {
  const { t } = useLingui();
  const ids = useId();
  const rpc = useSpaceRpc();
  const queries = useSpaceQueries();
  const queryClient = useQueryClient();
  const list = queries.botContacts.list.queryOptions({ input: { botId } });
  const contacts = useQuery(list).data;
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<string | "new" | null>(null);
  const [draft, setDraft] = useState<ContactDraft>(EMPTY_CONTACT);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const needle = query.trim().toLowerCase();
  const shown = (contacts ?? []).filter(
    (contact) =>
      !needle ||
      [contact.name, contact.email, contact.phone, contact.note].some((field) =>
        field.toLowerCase().includes(needle),
      ),
  );
  const refresh = () => queryClient.invalidateQueries({ queryKey: list.queryKey });
  const open = (contact: BotContact | null) => {
    setError(null);
    setEditing(contact?.id ?? "new");
    setDraft(contact ? { ...contact } : EMPTY_CONTACT);
  };
  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await refresh();
      setEditing(null);
    } catch (err) {
      setError(errorText(err, t`Could not save`));
    } finally {
      setBusy(false);
    }
  }
  const form = (
    <form
      className="space-y-3 px-4 py-3"
      data-testid="contact-form"
      onSubmit={(event) => {
        event.preventDefault();
        void run(() =>
          editing === "new"
            ? rpc.botContacts.create({ botId, ...draft })
            : rpc.botContacts.update({ contactId: editing!, ...draft }),
        );
      }}
    >
      {(
        [
          ["name", t`Name`, "text"],
          ["email", t`Email`, "email"],
          ["phone", t`Phone`, "tel"],
          ["note", t`Note`, "text"],
        ] as const
      ).map(([field, label, type]) => (
        <div key={field}>
          <label htmlFor={`${ids}-${field}`} className={fieldLabel}>
            {label}
          </label>
          <Input
            id={`${ids}-${field}`}
            type={type}
            required={field === "name"}
            value={draft[field]}
            onChange={(event) => setDraft({ ...draft, [field]: event.target.value })}
          />
        </div>
      ))}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(null)}>
          <Trans>Cancel</Trans>
        </Button>
        <Button type="submit" size="sm" disabled={busy || !draft.name.trim()}>
          <Trans>Save</Trans>
        </Button>
      </div>
    </form>
  );
  return (
    <div className="space-y-4" data-testid="bot-settings-address-book">
      <div className="flex gap-2">
        <SearchField value={query} onChange={setQuery} label={t`Search`} />
        <Button onClick={() => open(null)} disabled={editing === "new"}>
          <Plus className="size-4" />
          <Trans>Add contact</Trans>
        </Button>
      </div>
      {editing === "new" ? <SettingsGroup>{form}</SettingsGroup> : null}
      {contacts && !contacts.length && editing !== "new" ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          <Trans>No contacts yet</Trans>
        </p>
      ) : null}
      {shown.length ? (
        <SettingsGroup>
          {shown.map((contact) =>
            editing === contact.id ? (
              <div key={contact.id}>{form}</div>
            ) : (
              <SettingsRow
                key={contact.id}
                label={
                  <div className="min-w-0">
                    <p className="truncate">{contact.name}</p>
                    <p className="truncate text-[13px] text-muted-foreground">
                      {[contact.email, contact.phone].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                }
                control={
                  <>
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label={t`Edit ${contact.name}`}
                      onClick={() => open(contact)}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label={t`Remove ${contact.name}`}
                      disabled={busy}
                      onClick={() =>
                        void run(() => rpc.botContacts.remove({ contactId: contact.id }))
                      }
                    >
                      <X className="size-4" />
                    </Button>
                  </>
                }
              />
            ),
          )}
        </SettingsGroup>
      ) : null}
      {error ? <p className="text-[13px] text-destructive">{error}</p> : null}
    </div>
  );
}

export function ConversationSection({ actions }: SectionProps) {
  return (
    <SettingsGroup data-testid="bot-settings-conversation">
      <SettingsRow
        label={<Trans>Export</Trans>}
        control={
          <Button
            variant="outline"
            size="sm"
            onClick={() => void actions.onExport().catch(() => undefined)}
          >
            <Download className="size-4" />
            <Trans>Download</Trans>
          </Button>
        }
      />
      <SettingsRow
        label={<Trans>Clear conversation</Trans>}
        control={
          <Button variant="destructive" size="sm" onClick={actions.onClear}>
            <Trans>Clear</Trans>
          </Button>
        }
      />
    </SettingsGroup>
  );
}
